// Applies, the other half of the top bar's one control (ChangesAndApplies,
// #2336), on every page (ADR-0480 decision 4a, #2064): each apply this tab sent whose route answered `202`
// with the apply's id, and what its record says, until the operator dismisses
// it.
//
// **It reads when something says the record may have moved, and never on a
// clock** (§29). Once when an apply is handed to it, again when the change
// stream (`GET /v1/changes`, ADR-0240) announces that an apply's record moved
// — the announcement names a cell and no apply, so every open record is read
// — again when the stream reconnects, since what it announced while it was
// down is lost, and again when the operator moves between pages or makes a
// write (`shell/following.ts`'s triggers). The stream is held only while an
// apply is still open. Every one of those reads is asked by nobody and says so
// (`Zenith-Unattended`). A browser without `EventSource` reads on the other
// triggers alone.
//
// **A new tab finds them again** (ADR-0536 decisions 3a and 4a, #2223): on
// its first draw it reads the caller's open applies once (`findApplies`) and
// follows each this browser has not dismissed, a running one until it ends and
// an interrupted one until it is dismissed.
//
// **It draws the record's word and decides nothing.** `running` stays
// *running* however long it lasts; a record whose process is gone reads
// `interrupted` (ADR-0536 decision 1a), shown as *stopped*, and nothing
// resumes it (ADR-0480 decision 5a); `done` is the route's own answer, never
// the devices running it. A `404` is *no longer readable*: the record is answered under
// its own route's scope (§6.3), and that read is never asked again.
import { useCallback, useEffect } from "react";
import { Pill } from "../ui";
import { useMoved, useOnChange } from "./ChangesToApply";
import {
  applyOpen, findApplies, readApplies, readApply, stopFollowingApply, useFollowedApplies, wordsOf,
  type FollowedApply,
} from "./following";

const CHANGES = "/v1/changes";

const readAll = () => readApplies(readApply);

/** The applies half's state and reads, on every page: held by a component
 * that is never remounted, so a new tab's one read of the open applies is
 * asked once and the change stream is held only while an apply is open. */
export function useApplies() {
  const list = useFollowedApplies();
  const anyOpen = list.some(applyOpen);

  // A new tab: the caller's open applies, once (ADR-0536 decision 4a).
  useEffect(() => { void findApplies(); }, []);

  // An apply handed over is read at once (`followApply`); a move or a write reads again.
  useOnChange(useMoved(), useCallback(() => { void readAll(); }, []));

  useEffect(() => {
    if (!anyOpen || typeof EventSource === "undefined") return;
    const stream = new EventSource(CHANGES);
    let opened = false;
    stream.addEventListener("open", () => {
      // A reconnection: anything announced while the stream was down is lost.
      if (opened) void readAll();
      opened = true;
    });
    stream.addEventListener("change", (ev) => {
      try { if (JSON.parse((ev as MessageEvent).data).kind !== "apply") return; } catch { /* read anyway */ }
      void readAll();
    });
    return () => stream.close();
  }, [anyOpen]);

  const running = list.filter(applyOpen).length;
  const summary = list.length === 0 ? undefined : running > 0 ? `Applies: ${running} running` : `Applies: ${list.length} ended`;
  return { list, running, summary };
}

export type Applies = ReturnType<typeof useApplies>;

/** The applies followed, as one part of the panel. */
export function AppliesSection({ applies }: { applies: Applies }) {
  return (
    <div className="shell-activity-part shell-applies" data-open-applies={applies.running}>
      <p className="shell-activity-head">{applies.summary}</p>
      <ul className="shell-following-list">
        {applies.list.map((a) => <AppliedLine key={a.id} a={a} />)}
      </ul>
      {/* Restates ADR-0480 decisions 3a, 4a and 5a, and ADR-0536 decisions 1a and 4a — an apply's answer
          is Zenith accepting it; its outcome is the record's, read when the change stream says the record
          moved; a record whose process is gone reads stopped, nothing resumes it, and neither it nor a
          running one is shown as applied; a new tab finds the caller's open applies again. */}
      <p className="hint">
        An apply&rsquo;s answer means Zenith accepted it. What the controller, the switch or the router then did is
        read from the apply&rsquo;s own record, when the server says it moved and each time you move to another
        page. <em>Finished</em> is that answer, not a device running it: each device&rsquo;s page says what it runs.
        <em> Stopped</em> means a restart ended it before it finished, and nothing resumed it. Your running and
        stopped applies are listed again in a new tab until you dismiss them.
      </p>
    </div>
  );
}

/** One apply: what sent it, the record's word, and what it said. */
function AppliedLine({ a }: { a: FollowedApply }) {
  const open = applyOpen(a);
  const w = wordsOf(a);
  return (
    <li data-apply={a.id} data-state={a.gone ? "gone" : a.record?.state ?? "accepted"}>
      {a.what}: <Pill tone={w.tone}>{w.word}</Pill>
      {w.detail && <> {w.detail}</>}
      {a.problem && <span className="muted"> The last read failed ({a.problem}); it is read again on your next move.</span>}{" "}
      <button type="button" className="btn btn-quiet" onClick={() => stopFollowingApply(a.id)}>
        {open ? "Stop following" : "Dismiss"}
      </button>
    </li>
  );
}
